import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_RULES_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  FILTERS_UI_CONFIGS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
  statusCellCustomRenderer,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzExchangeAntimalwareRulesGridDataProvider } from './GzExchangeAntimalwareRulesGridDataProvider';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { GzExchangeAntimalwareRulesFormWebComponent } from './rulesItemForm';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import { ExchangeAntimalwareRule } from '@nirvana/shared/dtos/types/policies/common';
import { ExchangeAntimalwareRuleFormValue } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';
import { DefaultPolicyColumnKeys } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView/configs/column-config';
import { GzInputNumberWebComponent } from '../../../../../../../../ui/gzInput';
import { PolicyGridEvents } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/events/events';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_RULES_GRID)
export class GzExchangeAntimalwareRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ExchangeAntimalwareRule,
  ExchangeAntimalwareRuleFormValue,
  GzExchangeAntimalwareRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
    [ColumnKeys.STATUS]: statusCellCustomRenderer,
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
  protected filtersUiConfigs = FILTERS_UI_CONFIGS;

  protected hasSelectableRows = true;

  protected hasPriorityColumn = true;
  protected hasMoveToTopAction = true;
  protected hasCloneRowAction = true;

  protected translations = {
    editRow: translateFn('Edit priority'),
    deleteRow: translateFn('Delete'),
    moveToTop: translateFn('Move to top'),
    cloneRow: translateFn('Clone'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#clone-button')
  protected cloneButton: GzButtonWebComponent;

  protected getDataProvider(): GzExchangeAntimalwareRulesGridDataProvider {
    return new GzExchangeAntimalwareRulesGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button id="clone-button" type="${ButtonTypes.OUTLINE}" @click="${this.onCloneButtonClick.bind(this)}"
        >${translateFn('Clone')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete rules?'),
          translateFn('You are about to delete the selected rules. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ExchangeAntimalwareRuleFormValue): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzExchangeAntimalwareRulesGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ExchangeAntimalwareRuleFormValue,
    newItem: ExchangeAntimalwareRuleFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.name === oldItem.name) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);

    // check if default rule is selected
    const items = (this.gridComponent.dataProvider as GzExchangeAntimalwareRulesGridDataProvider).items;
    const isDefaultRuleSelected = event.detail.selectionStoreData.some(
      (selectedItemPosition: number) => items[selectedItemPosition - 1].default
    );

    // buttons should be disabled if default rule is selected
    this.deleteButton.disabled = !event.detail.selectionStoreData.length || isDefaultRuleSelected;
    this.cloneButton.disabled = event.detail.selectionStoreData.length !== 1 || isDefaultRuleSelected;
  }

  private onAddButtonClick(): void {
    // get max priority based on default rule if it exists
    const maxPriority = this.getMaxPriority(1);

    const addRuleCallback = (formValue: ExchangeAntimalwareRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareRulesFormWebComponent(maxPriority),
      strings: {
        title: translateFn('Add antimalware filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: ExchangeAntimalwareRuleFormValue): void {
    // get max priority based on default rule if it exists
    const maxPriority = this.getMaxPriority(item.default ? 1 : 0);

    const addRuleCallback = (formValue: ExchangeAntimalwareRuleFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareRulesFormWebComponent(maxPriority, item),
      strings: {
        title: translateFn('Edit antimalware filtering rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onCloneRow(event: CustomEvent) {
    this.cloneRule(event.detail.rowData.item);
  }

  private onCloneButtonClick(): void {
    if (!this.hasSelectableRows) {
      return;
    }

    // a single item can be cloned
    const itemPosition = this.selectedItemsGridData[0];
    const item = (this.gridComponent.dataProvider as GzExchangeAntimalwareRulesGridDataProvider).items[
      itemPosition - 1
    ];

    this.cloneRule(item);
  }

  private cloneRule(item: ExchangeAntimalwareRule): void {
    // set max priority
    const maxPriority = this.getMaxPriority(1);
    const addRuleCallback = (formValue: ExchangeAntimalwareRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create new item
    const newItem = {
      ...item,
      defaultPositionInGrid: 1, // by default, a new rule will be added at the top of the list
      name: this.getSuggestedNameForClonedItem(item.name, 'name'),
      default: false,
      active: true,
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareRulesFormWebComponent(maxPriority, newItem),
      strings: {
        title: translateFn('Add antimalware filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  /**
   * Override this method to add custom logic for activate inline menu event.
   * Default rule actions are disabled.
   * @param event
   * @protected
   */
  protected onActivateInlineMenu(event: CustomEvent) {
    const item = event.detail.itemData.item as ExchangeAntimalwareRuleFormValue;

    this.gridComponent.inlineRowActions?.forEach(action => {
      // move to top action is disabled for default rule and the rule that is already at the top
      if (action.value === PolicyGridEvents.MoveToTop) {
        action.disabled = item.default || item.defaultPositionInGrid === 1;
        return;
      }

      // other actions are disabled for default rule
      action.disabled = item.default;
    });
  }

  /**
   * Override this method to add custom logic for edit row enabled event.
   * Max priority is calculated based on the default rule.
   * @param event
   * @protected
   */
  protected onEditRowEnabled(event: CustomEvent) {
    if (!this.hasPriorityColumn) {
      return;
    }

    const currentRowIndex = event.detail.rowData.index;
    const minPriority = 1;
    const maxPriority = this.getMaxPriority(0);

    // set min and max priority for edited row
    const editedCells = this.gridComponent.controller?.view.getAllEditedCellsInRow(currentRowIndex + 2);
    editedCells?.forEach((gridCellEdit: GridCellEditWebComponent) => {
      const cellColumnKey = gridCellEdit.getAttribute('id');

      if (cellColumnKey === DefaultPolicyColumnKeys.POSITION) {
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).min = minPriority;
        (gridCellEdit.formField.Input as GzInputNumberWebComponent).max = maxPriority;
      }
    });
  }

  /**
   * Get max priority based on default rule if it exists.
   * @param offset - offset to be added to the max priority (when adding a new rule, offset is 1)
   * @private
   */
  private getMaxPriority(offset: number): number {
    const items = (this.gridComponent.dataProvider as GzExchangeAntimalwareRulesGridDataProvider).items;

    // get max priority
    const maxPriority = items.length + offset;
    // get default rule index - if no default rule, this will be -1
    const defaultRuleIndex = items.findIndex(item => item.default);

    // if no default rule, return max priority as it is
    if (defaultRuleIndex === -1) {
      return maxPriority;
    }

    // a new rule should have a priority lower than the default rule
    // Note: when exists, default rule is the last one (checked in production)
    return defaultRuleIndex + offset;
  }
}
